import { faEllipsis, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Toast } from '../../../components/Common/Toast';
import { useStudentVPCStatus } from '../../../hooks/useStudentVPCStatus';
import { BookModel } from '../../../interfaces/book.interface';
import { UserModel, UserRoleEnum } from '../../../interfaces/user.interface';
import { openPlanPurchaseModal } from '../../../store/appSlice';
import { AppDispatch, RootState } from '../../../store/store';
import { getOnboardingResumeRoute } from '../../Teacher/utils/onboarding.utils';
import ListViewModeSwitcher, { ListViewMode } from './ListViewModeSwitcher';

export interface MainBookControlBarProps {
  showListModeSwitcher?: boolean;
  mode?: ListViewMode;
  setMode?: (mode: ListViewMode) => void;
  onShare?: () => void;
  mainButtonDisabled?: boolean;
  bookData: BookModel | null;
  user: UserModel | null;
  isTeacherOnboarding?: boolean;
}

const MainBookControlBar = ({
  showListModeSwitcher,
  mode,
  setMode,
  onShare,
  mainButtonDisabled,
  bookData,
  user,
  isTeacherOnboarding,
}: MainBookControlBarProps) => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const { vpcRequired, vpcComplete, teacherState, loading, isVPCShareBlocked } = useStudentVPCStatus();

  const showSubscriptionPopup = () => {
    dispatch(
      openPlanPurchaseModal({
        show: true,
        data: {
          title: 'Finish your story!',
          description:
            'Unlock your last images and finish your personalized book by ordering a physical copy, or join a subscription plan to create more books for your library and print your favorites!',
        },
        isFinishStory: true,
      }),
    );
  };

  const printStory = () => {
    if (mainButtonDisabled) {
      showWaitNotification();
      return;
    }
    navigateTo(`/bookpurchase/${bookData?.bookId}?mode=preview`);
  };

  const showWaitNotification = () => {
    Toast('Please wait for images to load before selecting this option.', true);
  };

  const shareBook = () => {
    if (mainButtonDisabled) {
      showWaitNotification();
      return;
    }
    onShare && onShare();
  };

  const previewBook = () => {
    if (mainButtonDisabled) {
      showWaitNotification();
      return;
    }
    navigateTo(`/bookflip/${bookData?.bookId}?mode=preview`);
  };

  if (isTeacherOnboarding) {
    return (
      <div className="sticky top-0 bg-gradient-to-r from-storyblueSecondLightText to-storyblueButton text-white p-2.5 shadow-md z-30">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center">
            {showListModeSwitcher && mode && (
              <div className="mr-5 md:flex hidden">
                <ListViewModeSwitcher
                  mode={mode}
                  setMode={(m) => setMode && setMode(m)}
                  colorIcon={'white'}
                  colorActiveIcon={'white'}
                  colorBackground={'storyblueSecondLightText'}
                />
              </div>
            )}
          </div>
          <div className="flex items-center">
            <button
              onClick={() => navigateTo(getOnboardingResumeRoute(teacherOnboarding))}
              className="bg-white px-5 py-2 rounded-full text-sm font-semibold text-storyblueSecondLightText hover:text-storyblueSecondLightTextBold transition"
              style={{ border: '1px solid rgba(74, 168, 175, 1)' }}
            >
              Next: Complete Setup
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 bg-gradient-to-r from-storyblueSecondLightText to-storyblueButton text-white p-2.5 shadow-md z-30">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center">
          {showListModeSwitcher && mode && (
            <div className="mr-5 md:flex hidden">
              <ListViewModeSwitcher
                mode={mode}
                setMode={(mode) => setMode && setMode(mode)}
                colorIcon={'white'}
                colorActiveIcon={'white'}
                colorBackground={'storyblueSecondLightText'}
              ></ListViewModeSwitcher>
            </div>
          )}

          <div className="flex items-center md:space-x-5">
            <div className="w-px h-5 bg-white opacity-40 md:flex hidden" />

            <button
              // onClick={onHome}
              aria-label="Go to Home"
              className="hover:opacity-80 transition-opacity"
            >
              <a
                href="https://buildmystory.com/guide/"
                target="_blank"
                rel="noopener noreferrer"
                title="Guide"
                className="border rounded-full border-white px-1.5 py-0.5"
              >
                <FontAwesomeIcon
                  icon={faQuestion}
                  className={`cursor-pointer text-white`}
                  title="info"
                  onClick={() => {}}
                />
              </a>
            </button>
          </div>
        </div>

        <div className="flex items-center">
          <Menu>
            <Menu.Button className="flex items-center space-x-5 md:hidden">
              <FontAwesomeIcon icon={faEllipsis} className="cursor-pointer text-white text-2xl" title="Menu" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 top-[2rem] w-36 z-0 origin-top-right bg-gray-100 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="px-1 py-1 flex flex-col items-baseline">
                <Menu.Item>
                  {({ active }) => (
                    <div className="flex items-baseline">
                      <Link
                        to={mainButtonDisabled ? '' : `/bookflip/${bookData?.bookId}?mode=preview`}
                        onClick={() => (mainButtonDisabled ? previewBook() : null)}
                        title="Open Flipbook view page"
                        className="w-full h-8  text-storygreen text-xs  hover:text-storyblue leading-none font-bold p-2 capitalize"
                      >
                        View Flipbook
                      </Link>
                    </div>
                  )}
                </Menu.Item>
                {user?.role !== UserRoleEnum.student && (
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex items-baseline">
                        <Link
                          to={``}
                          onClick={shareBook}
                          title="Share this book"
                          className="w-full h-8  text-storygreen text-xs  hover:text-storyblue leading-none font-bold p-2 capitalize"
                        >
                          Share
                        </Link>
                      </div>
                    )}
                  </Menu.Item>
                )}
                {user?.role === UserRoleEnum.student && !loading && !isVPCShareBlocked() && (
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex items-baseline">
                        <Link
                          to={``}
                          onClick={shareBook}
                          title="Share this book"
                          className="w-full h-8  text-storygreen text-xs  hover:text-storyblue leading-none font-bold p-2 capitalize"
                        >
                          Share
                        </Link>
                      </div>
                    )}
                  </Menu.Item>
                )}
                {user?.role !== UserRoleEnum.student && (
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex items-baseline">
                        {user?.isTrialFreeAccess && (
                          <Link
                            to={``}
                            onClick={showSubscriptionPopup}
                            title="Continue"
                            className="text-storygreen  hover:text-storyblue leading-none font-bold rounded-2xl px-4 py-2 text-sm capitalize"
                          >
                            Continue
                          </Link>
                        )}
                        {!user?.isTrialFreeAccess && (
                          <Link
                            to={mainButtonDisabled ? '' : `/bookpurchase/${bookData?.bookId}?mode=preview`}
                            title="Print story"
                            onClick={() => (mainButtonDisabled ? printStory() : null)}
                            className="text-storygreen  hover:text-storyblue leading-none font-bold rounded-2xl px-4 py-2 text-sm capitalize"
                          >
                            Print story
                          </Link>
                        )}
                      </div>
                    )}
                  </Menu.Item>
                )}
                {/*{user?.role === UserRoleEnum.student && !isVPCBlocked() && (*/}
                {/*  <Menu.Item>*/}
                {/*    {({ active }) => (*/}
                {/*      <div className="flex items-baseline">*/}
                {/*        <Link*/}
                {/*          to={mainButtonDisabled ? '' : `/bookpurchase/${bookData?.bookId}?mode=preview`}*/}
                {/*          title="Print story"*/}
                {/*          onClick={() => (mainButtonDisabled ? printStory() : null)}*/}
                {/*          className="text-storygreen  hover:text-storyblue leading-none font-bold rounded-2xl px-4 py-2 text-sm capitalize"*/}
                {/*        >*/}
                {/*          Print story*/}
                {/*        </Link>*/}
                {/*      </div>*/}
                {/*    )}*/}
                {/*  </Menu.Item>*/}
                {/*)}*/}
              </div>
            </Menu.Items>
          </Menu>

          <div className="hidden sm:flex items-center space-x-5">
            <button
              onClick={previewBook}
              aria-label="Flipbook"
              title="Show Flipbook view page"
              className="hover:opacity-80 transition-opacity"
            >
              <img src="/controls/visibility-icon.svg" alt="" className="w-6" />
            </button>

            {user?.role !== UserRoleEnum.student && (
              <button
                onClick={shareBook}
                aria-label="Share"
                title="Share this book"
                className="hover:opacity-80 transition-opacity"
              >
                <img src="/controls/share-icon.svg" alt="share" className="w-6" />
              </button>
            )}
            {user?.role === UserRoleEnum.student && !loading && !isVPCShareBlocked() && (
              <button
                onClick={shareBook}
                aria-label="Share"
                title="Share this book"
                className="hover:opacity-80 transition-opacity"
              >
                <img src="/controls/share-icon.svg" alt="share" className="w-6" />
              </button>
            )}
            {user?.isTrialFreeAccess && user?.role !== UserRoleEnum.student && (
              <button
                id="step-next-button-main-bar"
                onClick={showSubscriptionPopup}
                title="Continue"
                className="bg-white px-10 py-2.5 rounded-full text-sm font-semibold hover:text-storyblueSecondLightTextBold text-storyblueSecondLightText transition"
                style={{ border: '1px solid rgba(74, 168, 175, 1)' }}
              >
                Continue
              </button>
            )}
            {!user?.isTrialFreeAccess && user?.role !== UserRoleEnum.student && (
              <button
                id="step-next-button-main-bar"
                onClick={printStory}
                title="Print story"
                className="bg-white px-4 py-2 rounded-full text-sm font-semibold hover:text-storyblueSecondLightTextBold text-storyblueSecondLightText transition"
                style={{ border: '1px solid rgba(74, 168, 175, 1)' }}
              >
                Print Story
              </button>
            )}
            {/*{user?.role === UserRoleEnum.student && !isVPCBlocked() && (*/}
            {/*  <button*/}
            {/*    onClick={printStory}*/}
            {/*    title="Print story"*/}
            {/*    className="bg-white px-4 py-2 rounded-full text-sm font-semibold hover:text-storyblueSecondLightTextBold text-storyblueSecondLightText transition"*/}
            {/*    style={{ border: '1px solid rgba(74, 168, 175, 1)' }}*/}
            {/*  >*/}
            {/*    Print Story*/}
            {/*  </button>*/}
            {/*)}*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainBookControlBar;
